import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStateContext } from '../contexts/ContextProvider';
import { signOut } from '../lib/appwrite';
import { AiOutlineMenu, AiOutlineClose } from 'react-icons/ai';
import { CgLogOut, CgProfile } from 'react-icons/cg';
import { MdAccountCircle, MdOutlineMessage, MdOutlinePhotoSizeSelectActual } from 'react-icons/md';

const AdminLayout = ({ children }) => {
  const { activeMenu, setActiveMenu, screenSize, setAccount, setIsLoggedIn, account } = useStateContext();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await signOut();
    setAccount(null);
    setIsLoggedIn(false);
    navigate('/');
  };

  const handleAccountClick = () => {
    setShowProfileMenu(false);
    navigate('/admin/account');
  };

  return (
    <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
      {/* Left Sidebar */}
      <div
        className={`${
          activeMenu ? 'w-64' : 'w-0'
        } bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden`}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Brand */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <Link to="/admin" className="text-xl font-bold text-gray-800 dark:text-white">
                Wildlife Comforts
              </Link>
              <button
                onClick={() => setActiveMenu(false)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Collapse sidebar"
              >
                <AiOutlineMenu className="text-xl" />
              </button>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-4 space-y-2">
            <Link
              to="/admin"
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/admin' || location.pathname === '/admin/dashboard' || location.pathname === '/'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span>Dashboard</span>
            </Link>
            <Link
              to="/admin/testimonials"
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/admin/testimonials'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <MdOutlineMessage className="text-xl" />
              <span>Testimonials</span>
            </Link>
            <Link
              to="/admin/photos"
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/admin/photos'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <MdOutlinePhotoSizeSelectActual className="text-xl" />
              <span>Photos</span>
            </Link>
            <Link
              to="/admin/users"
              className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                location.pathname === '/admin/users'
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <MdAccountCircle className="text-xl" />
              <span>Users</span>
            </Link>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-2 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <CgLogOut />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Menu Toggle (only show when sidebar is collapsed) */}
            <div className="flex-1">
              {!activeMenu && (
                <button
                  onClick={() => setActiveMenu(true)}
                  className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <AiOutlineMenu className="text-xl" />
                </button>
              )}
            </div>

            {/* Right: Profile Menu - Always visible */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <MdAccountCircle className="text-2xl text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {account?.username || 'User'}
                </span>
              </button>

              {/* Profile Dropdown Menu */}
              {showProfileMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowProfileMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    <div className="py-1">
                      <button
                        onClick={handleAccountClick}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <CgProfile />
                        <span>Account</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <CgLogOut />
                        <span>Logout</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

